import { 
  faChevronDown, 
  faChevronUp, 
  faInfo, 
  faEye,
  faChartLine,
  faRoute,
  faTruck,
  faExclamationTriangle,
  faComment,
  faCog,
  faLightbulb
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState } from 'react';
import { Container, Card } from 'react-bootstrap';
import iconHelp from '../../assets/images/icons/icon-help.png';
import './help.css';

// Données des éléments d'aide organisées par catégories
const helpCategories = [
  {
    id: 'overview',
    title: 'Vue d\'ensemble',
    icon: faEye,
    items: [
      {
        id: 1,
        question: "À quoi sert le Back-office ?",
        content: (
          <>
            <p>Le Back-office est une plateforme de gestion complète de votre flotte de livraison. Elle vous permet de :</p>
            <ul>
              <li><strong>Consulter</strong> les informations détaillées des livraisons.</li>
              <li><strong>Suivre</strong> les métriques en temps réel (livraisons, ponctualité, performances).</li>
              <li><strong>Optimiser</strong> vos tournées et vos livraisons.</li>
              <li><strong>Gérer</strong> votre flotte de véhicules et les codes PIN associés.</li>
              <li><strong>Analyser</strong> les données avec les tableaux de bord interactifs.</li>
            </ul>
          </>
        )
      }
    ]
  },
  {
    id: 'dashboard',
    title: 'Tableau de bord',
    icon: faChartLine,
    items: [
      {
        id: 10,
        question: "Qu'est-ce que le Tableau de bord ?",
        content: (
          <>
            <p>Le Tableau de bord est votre centre de contrôle centralisé. Il vous affiche :</p>
            <ul>
              <li><strong>KPI clés :</strong> Nombre de livraisons du jour, taux de ponctualité, statut général.</li>
              <li><strong>Graphiques :</strong> Visualisez les métriques avec des graphiques en barres et camemberts.</li>
              <li><strong>Carte en temps réel :</strong> Position et statut des véhicules sur la map.</li>
              <li><strong>État de la flotte :</strong> Véhicules disponibles, en tournée, en maintenance.</li>
              <li><strong>Alertes :</strong> Notifié des anomalies et problèmes.</li>
            </ul>
          </>
        )
      },
      {
        id: 11,
        question: "Comment interpréter les métriques du tableau de bord ?",
        content: (
          <>
            <p>Les métriques principales sont :</p>
            <ul>
              <li><strong>Livraisons :</strong> Nombre total d'étapes livrées dans la journée.</li>
              <li><strong>Ponctualité :</strong> Pourcentage des livraisons effectuées à l'heure.</li>
              <li><strong>Statuts :</strong> En attente, En cours, Terminée, Anomalie.</li>
              <li><strong>Performance :</strong> Comparaison jour/jour des indicateurs clés.</li>
            </ul>
          </>
        )
      }
    ]
  },
  {
    id: 'tours',
    title: 'Tournées',
    icon: faRoute,
    items: [
      {
        id: 20,
        question: "Comment fonctionnent les tournées ?",
        content: (
          <>
            <p>Une tournée est une série d'étapes de livraison à effectuer par un véhicule :</p>
            <ul>
              <li><strong>Accédez</strong> à la section "Tournées" via le menu de gauche.</li>
              <li><strong>Consultez</strong> la liste des tournées du jour avec leur statut.</li>
              <li><strong>Cliquez</strong> sur une tournée pour voir les détails.</li>
              <li><strong>Observez</strong> les différentes étapes de livraison et leur progression.</li>
            </ul>
          </>
        )
      },
      {
        id: 21,
        question: "Quels sont les statuts des tournées ?",
        content: (
          <>
            <p>Chaque tournée a un statut qui indique son avancement :</p>
            <ul>
              <li><strong>Créée :</strong> La tournée a été créée mais pas encore démarrée.</li>
              <li><strong>Démarrée :</strong> Le véhicule a commencé la tournée.</li>
              <li><strong>En cours :</strong> Au moins une étape est en cours de livraison.</li>
              <li><strong>Terminée :</strong> Tous les colis ont été livrés.</li>
              <li><strong>Problème :</strong> Une anomalie a été signalée durant la tournée.</li>
            </ul>
          </>
        )
      },
      {
        id: 22,
        question: "Comment filtrer les tournées ?",
        content: (
          <>
            <p>Oui, vous pouvez filtrer les tournées selon plusieurs critères :</p>
            <ul>
              <li><strong>Par statut :</strong> Utilisez le bouton de filtre en haut à droite.</li>
              <li><strong>Par date :</strong> Sélectionnez une plage de dates.</li>
              <li><strong>Par recherche :</strong> Entrez un numéro ou nom de tournée.</li>
              <li><strong>Vue d'ensemble :</strong> Sélectionnez le filtre "Toutes" pour voir toutes les tournées.</li>
            </ul>
          </>
        )
      },
      {
        id: 23,
        question: "Comment consulter les détails d'une tournée ?",
        content: (
          <>
            <p>Pour accéder aux détails d'une tournée :</p>
            <ul>
              <li><strong>Cliquez</strong> sur le bouton "Info" (<FontAwesomeIcon icon={faInfo} />) d'une tournée.</li>
              <li><strong>Consultez</strong> les différentes étapes de livraison.</li>
              <li><strong>Vérifiez</strong> le statut, les adresses et les dates de livraison.</li>
              <li><strong>Accédez</strong> à la liste des BL (Bons de Livraison) attachés à chaque étape.</li>
              <li><strong>Cliquez</strong> sur une étape pour voir les BL spécifiques.</li>
            </ul>
          </>
        )
      }
    ]
  },
  {
    id: 'vehicles',
    title: 'Véhicules',
    icon: faTruck,
    items: [
      {
        id: 30,
        question: "Où gérer ma flotte de véhicules ?",
        content: (
          <>
            <p>La section "Véhicules" vous permet de gérer toute votre flotte :</p>
            <ul>
              <li><strong>Accédez</strong> à "Véhicules" via le menu de gauche.</li>
              <li><strong>Consultez</strong> la liste de tous vos véhicules.</li>
              <li><strong>Visualisez</strong> les métriques de chaque véhicule (livraisons, ponctualité).</li>
              <li><strong>Recherchez</strong> un véhicule spécifique par immatriculation.</li>
            </ul>
          </>
        )
      },
      {
        id: 31,
        question: "Comment changer le code PIN d'un véhicule ?",
        content: (
          <>
            <p>Pour modifier le code PIN d'un véhicule :</p>
            <ul>
              <li><strong>Rendez-vous</strong> dans "Véhicules".</li>
              <li><strong>Cliquez</strong> sur le bouton "Info" (<FontAwesomeIcon icon={faInfo} />) du véhicule.</li>
              <li><strong>Accédez</strong> au champ "Code PIN".</li>
              <li><strong>Modifiez</strong> le code en respectant les règles :</li>
              <ul>
                <li>Le code doit être composé <strong>uniquement de chiffres</strong>.</li>
                <li>Le code doit faire <strong>exactement 6 caractères</strong>.</li>
              </ul>
              <li><strong>Validez :</strong> Le code est enregistré automatiquement.</li>
              <li><strong>Erreur :</strong> Si un problème survient, un message d'erreur s'affichera.</li>
            </ul>
          </>
        )
      },
      {
        id: 32,
        question: "Quelles informations puis-je consulter sur un véhicule ?",
        content: (
          <>
            <p>En cliquant sur un véhicule, vous accédez à :</p>
            <ul>
              <li><strong>Immatriculation :</strong> Le numéro d'identification du véhicule.</li>
              <li><strong>Statut :</strong> Disponible, En tournée, Maintenance.</li>
              <li><strong>Livraisons :</strong> Nombre d'étapes livrées.</li>
              <li><strong>Ponctualité :</strong> Pourcentage de livraisons à l'heure.</li>
              <li><strong>Dernière tournée :</strong> Date et heure de la dernière mission.</li>
              <li><strong>Kilométrage :</strong> Si la fonctionnalité est activée.</li>
              <li><strong>Code PIN :</strong> Pour les accès de sécurité.</li>
            </ul>
          </>
        )
      }
    ]
  },
  {
    id: 'reserves',
    title: 'Réserves et Anomalies',
    icon: faExclamationTriangle,
    items: [
      {
        id: 40,
        question: "Qu'est-ce qu'une réserve ou anomalie ?",
        content: (
          <>
            <p>Une réserve (ou anomalie) est une situation problématique durant une livraison :</p>
            <ul>
              <li><strong>Absence du client :</strong> Le destinataire n'était pas présent.</li>
              <li><strong>Adresse non trouvée :</strong> L'adresse est incorrecte ou inexistante.</li>
              <li><strong>Colis endommagé :</strong> Le colis a subi des dommages.</li>
              <li><strong>Refus de livraison :</strong> Le client refuse la livraison.</li>
              <li><strong>Autre :</strong> Tout autre problème non listé.</li>
            </ul>
          </>
        )
      },
      {
        id: 41,
        question: "Comment gérer une réserve sur une livraison ?",
        content: (
          <>
            <p>Si une anomalie est rencontrée :</p>
            <ul>
              <li><strong>Allez</strong> à la section "Réserves".</li>
              <li><strong>Consultez</strong> la liste des anomalies déclarées.</li>
              <li><strong>Sélectionnez</strong> une anomalie pour voir les détails.</li>
              <li><strong>Prenez une action :</strong> Relivraison, Remboursement, Retour, etc.</li>
              <li><strong>Documentez</strong> les actions prises avec des photos ou notes.</li>
            </ul>
          </>
        )
      }
    ]
  },
  {
    id: 'messaging',
    title: 'Messagerie',
    icon: faComment,
    items: [
      {
        id: 50,
        question: "Comment utiliser la messagerie ?",
        content: (
          <>
            <p>La section "Messagerie" permet la communication directe avec les chauffeurs :</p>
            <ul>
              <li><strong>Accédez</strong> à "Messagerie" dans le menu.</li>
              <li><strong>Créez</strong> un nouveau message ou une conversation.</li>
              <li><strong>Communiquez</strong> en temps réel avec les équipes sur le terrain.</li>
              <li><strong>Envoyez</strong> des instructions ou des modifications d'itinéraire.</li>
              <li><strong>Consultez</strong> l'historique des communications.</li>
            </ul>
          </>
        )
      }
    ]
  },
  {
    id: 'admin',
    title: 'Administration',
    icon: faCog,
    items: [
      {
        id: 60,
        question: "Comment gérer les utilisateurs ?",
        content: (
          <>
            <p>Section réservée aux administrateurs pour gérer les accès :</p>
            <ul>
              <li><strong>Créer un utilisateur :</strong> Nouvelle personne accède au système.</li>
              <li><strong>Modifier les rôles :</strong> Admin, Responsable, Opérateur.</li>
              <li><strong>Gérer les permissions :</strong> Qui peut voir quoi et faire quoi.</li>
              <li><strong>Supprimer un utilisateur :</strong> Révoquer l'accès.</li>
              <li><strong>Réinitialiser les mots de passe :</strong> En cas de perte.</li>
            </ul>
          </>
        )
      },
      {
        id: 61,
        question: "Comment importer des tournées via Excel ?",
        content: (
          <>
            <p>La section "Import Excel" permet l'import de masse :</p>
            <ul>
              <li><strong>Allez</strong> à "Gestion" → "Import Excel".</li>
              <li><strong>Téléchargez</strong> le template d'exemple.</li>
              <li><strong>Remplissez</strong> le fichier avec vos données de tournées.</li>
              <li><strong>Validez</strong> les données avant l'import.</li>
              <li><strong>Importez</strong> le fichier dans le système.</li>
              <li><strong>Consultez</strong> le rapport d'import avec les résultats.</li>
            </ul>
          </>
        )
      },
      {
        id: 62,
        question: "Où gérer les adresses et contacts ?",
        content: (
          <>
            <p>Sections de configuration pour les données de base :</p>
            <ul>
              <li><strong>Destinataires :</strong> Base de données des adresses de livraison.</li>
              <li><strong>Numéros de téléphone :</strong> Contacts importants et numéros d'urgence.</li>
              <li><strong>Catégories d'anomalies :</strong> Types de problèmes possibles.</li>
              <li><strong>Documents :</strong> Factures, bons de livraison, rapports.</li>
            </ul>
          </>
        )
      }
    ]
  },
  {
    id: 'faq',
    title: 'Astuces & Dépannage',
    icon: faLightbulb,
    items: [
      {
        id: 70,
        question: "Quelles sont les bonnes pratiques pour optimiser les livraisons ?",
        content: (
          <>
            <p>Conseils pour améliorer votre logistique :</p>
            <ul>
              <li><strong>Planification :</strong> Préparez les tournées bien à l'avance.</li>
              <li><strong>Regroupement :</strong> Combinez les adresses proches ensemble.</li>
              <li><strong>Monitoring :</strong> Consultez le tableau de bord régulièrement.</li>
              <li><strong>Réactivité :</strong> Utilisez la messagerie pour ajuster les tournées.</li>
              <li><strong>Analyse :</strong> Examinez les rapports et métriques quotidiennement.</li>
            </ul>
          </>
        )
      },
      {
        id: 71,
        question: "Que faire en cas de problème technique ?",
        content: (
          <>
            <p>Actions à prendre si vous rencontrez un bug :</p>
            <ul>
              <li><strong>Rafraîchir :</strong> Rechargez la page (F5 ou Cmd+R).</li>
              <li><strong>Vider le cache :</strong> Utilisez le mode incognito si le problème persiste.</li>
              <li><strong>Vérifier la connexion :</strong> Assurez-vous d'avoir une connexion Internet stable.</li>
              <li><strong>Consulter les logs :</strong> Allez à "Support" → "Statut API" pour vérifier l'état du système.</li>
              <li><strong>Contacter le support :</strong> Si le problème persiste, contactez votre administrateur.</li>
            </ul>
          </>
        )
      }
    ]
  }
];

export default function Help() {
  const [openItems, setOpenItems] = useState(new Set());
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Fonction pour basculer l'état d'ouverture d'un élément
  const toggleItem = (itemId) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(itemId)) {
      newOpenItems.delete(itemId);
    } else {
      newOpenItems.add(itemId);
    }
    setOpenItems(newOpenItems);
  };


  // Fonction pour changer la catégorie sélectionnée
  const handleCategoryFilter = (categoryId) => {
    if (selectedCategory === categoryId) {
      setSelectedCategory(null);
    } else {
      setSelectedCategory(categoryId);
    }
  };

  // Filtrer les catégories à afficher
  const filteredCategories = selectedCategory 
    ? helpCategories.filter(cat => cat.id === selectedCategory)
    : helpCategories;

  return (
    <Container style={{ width: '100%', maxWidth: '1100px' }}>
      <div className="img-fix" />
      <div className="text-center py-5 mb-5">
        <h1 className="title mb-2">Centre d'aide</h1>
        <p className="text-muted">Documentation complète de votre plateforme de gestion de flotte</p>
      </div>

      {/* Filtre par catégorie */}
      <div className="filter-pills-container mb-5">
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <button
            className={`filter-pill ${selectedCategory === null ? 'active' : ''}`}
            onClick={() => setSelectedCategory(null)}
          >
            Toutes les catégories
          </button>
          {helpCategories.map((category) => (
            <button
              key={category.id}
              className={`filter-pill ${selectedCategory === category.id ? 'active' : ''}`}
              onClick={() => handleCategoryFilter(category.id)}
            >
              <FontAwesomeIcon icon={category.icon} className="me-2" />
              {category.title}
            </button>
          ))}
        </div>
      </div>

      <div className="help-categories">
        {filteredCategories.map((category) => {
          return (
            <div key={category.id} className="category-section mb-5">
                <div className="help-accordion">
                  {category.items.map((item) => {
                    const isOpen = openItems.has(item.id);
                    return (
                      <Card 
                        key={item.id} 
                        className="help-card mb-3 shadow-sm border-0"
                        style={{ overflow: 'hidden' }}
                      >
                        <Card.Header 
                          className="help-card-header bg-white border-0 p-0"
                          onClick={() => toggleItem(item.id)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="d-flex align-items-center justify-content-between p-4 hover-bg-light">
                            <div className="d-flex align-items-center">
                              <img 
                                className="icon-help tw-w-5 tw-h-5 me-3" 
                                src={iconHelp} 
                                alt="aide" 
                              />
                              <h5 className="mb-0 tw-text-sm tw-text-gray-700">
                                {item.question}
                              </h5>
                            </div>
                            <FontAwesomeIcon 
                              icon={isOpen ? faChevronUp : faChevronDown} 
                              className="tw-w-3 tw-h-3"
                            />
                          </div>
                        </Card.Header>
                        
                        <div 
                          className={`help-content-wrapper ${isOpen ? 'open' : 'closed'}`}
                          style={{
                            maxHeight: isOpen ? '1000px' : '0px',
                            transition: 'max-height 0.4s ease-in-out',
                            overflow: 'hidden'
                          }}
                        >
                          <Card.Body className="pt-4 pb-3 px-4">
                            <div className="help-content text-muted">
                              {item.content}
                            </div>
                            <div className="help-card-footer mt-3 pt-3">
                              <span className="category-badge">
                                <FontAwesomeIcon icon={category.icon} className="me-2" />
                                {category.title}
                              </span>
                            </div>
                          </Card.Body>
                        </div>
                      </Card>
                    );
                  })}
                </div>
            </div>
          );
        })}
      </div>
    </Container>
  );
}
